import { useChallenges } from '@/src/store/challenge.context';
import { useUser } from '@/src/store/user.context';
import {
  Badge,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  Icon,
  Link,
  SpaceBetween,
  Toggle,
} from '@amzn/awsui-components-react';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { config } from '../../../config/app-config';
import LocalizedTimeAgo from '../LocalizedTimeAgo';
import { useApi } from '../../../store/api.context';
import { useCampaigns } from '../../../store/campaigns.context';
import { useEditCampaign } from '../../../store/edit-campaign.context';
import { EditEventActions, useEditEvent } from '../../../store/edit-event.context';
import { useEvents } from '../../../store/events.context';
import { Campaign } from '../../../types/Campaign';
import { ChallengeDescriptor, ChallengeListItem } from '../../../types/Challenge';
import { Nullable } from '../../../types/common';
import { Event } from '../../../types/Event';
import { formatLabProviderLabel } from '../../../types/LabProvider';
import { i18nKeys } from '../../../utils/i18n.utils';
import { getDuration } from '../../../utils/time.utils';
import DifficultyIndicator from '../../challenges/challengesCommon/difficultyIndiciator/DifficultyIndicator';
import { HorizontalRule } from '../HorizontalRule';
import { KeyValue } from '../KeyValue';
import StarRating from '../StarRating';
import ChallengeCardAdvanced from './ChallengeCardAdvanced';
import ReplaceChallengeWithBackupModal from './ReplaceChallengeWithBackupModal';
import PerChallengeBackupChallenges from './PerChallengeBackupChallenges';

interface ChallengeCardProps {
  challenge: ChallengeListItem;
  index: number;
  getChallengePointsPossible?: (challenge: Nullable<ChallengeListItem>) => number;
  getDefaultChallengePointsPossible?: (challenge: ChallengeListItem) => number;
  handleRemoveChallengeAction?: (challengeId: string) => void;
  toggleChallengeInfo: (challengeListItem: ChallengeListItem) => void;
  advancedMode?: boolean;
  target: Event | Campaign;
  backupChallenges?: boolean;
  challengeDescriptor: ChallengeDescriptor | null;
}

const ChallengeCard: React.FC<ChallengeCardProps> = ({
  challenge,
  index,
  getChallengePointsPossible,
  getDefaultChallengePointsPossible,
  handleRemoveChallengeAction,
  toggleChallengeInfo,
  challengeDescriptor,
  advancedMode = false,
  target,
  backupChallenges = false,
}) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { handleUpdateEditEvent, editMode } = useEditEvent();
  const { campaignEditMode } = useEditCampaign();
  const { event } = useEvents();
  const { campaign } = useCampaigns();
  const { eventsApi } = useApi();
  const { updateChallengeDescriptor } = useChallenges();
  const [replaceBackupModalVisible, setReplaceBackupModalVisible] = useState(false);

  const isEvent = target instanceof Event;

  const isAdmin = user && target?.isAdmin(user);

  const handleDownloadImportedCSV = () => {
    if (event && challenge.challengeId) {
      void eventsApi.downloadTeamChallengeProperties(event.name, challenge?.challengeId);
    }
  };

  const canEditWithAttribute = (attribute: string) => {
    if (user) {
      return (
        editMode &&
        (isAdmin || user.isSuperAdmin || user.isEventAdmin || (target as Event)?.hasOwnerPermission(user?.email)) &&
        target.canEditAttribute(attribute, user)
      );
    } else {
      return false;
    }
  };

  const handleEditEvent = (newChallengeDescriptor: ChallengeDescriptor) => {
    updateChallengeDescriptor(newChallengeDescriptor);
    const indexOfChallengeDescriptor = target.challengeDescriptors.findIndex(
      (cd: ChallengeDescriptor) => cd.challengeId === newChallengeDescriptor.challengeId
    );
    const newChallengeDescriptors = _.cloneDeep(target.challengeDescriptors);
    newChallengeDescriptors[indexOfChallengeDescriptor] = newChallengeDescriptor;
    handleUpdateEditEvent(EditEventActions.CHALLENGE_DESCRIPTORS, newChallengeDescriptors);
  };

  const toggleCodeWhispererDisabled = () => {
    const modifiedDescriptor = challengeDescriptor;
    if (modifiedDescriptor) {
      modifiedDescriptor.codeWhispererDisabled = !challengeDescriptor?.codeWhispererDisabled;
      handleEditEvent(modifiedDescriptor);
    }
  };

  const canRemoveChallenge =
    challenge &&
    challenge.challengeId !== null &&
    (editMode || campaignEditMode) &&
    !backupChallenges &&
    handleRemoveChallengeAction;

  const warmupChallenge =
    event?.challengeDescriptors.find((descriptor) => descriptor.challengeId === challenge?.challengeId)?.overrides
      ?.challengeAlwaysOn ??
    challenge.challengeAlwaysOn ??
    false;

  const availableBackupCount = (() => {
    if (!isEvent || !challenge.challengeId) {
      return 0;
    }
    const backupConfig = (target as Event).backupChallengeConfig;
    if (!backupConfig) {
      return 0;
    }
    const selectedChallengeIds = new Set(
      (target as Event).challengeDescriptors.map((cd) => cd.challengeId).filter((id): id is string => !!id)
    );

    // Per-challenge backup ids of UNSELECTED challenges are excluded from the general pool.
    const perChallengeBackupIdsOfUnselected = new Set<string>();
    Object.entries(backupConfig.perChallengeBackups ?? {}).forEach(([ownerChallengeId, backups]) => {
      if (!selectedChallengeIds.has(ownerChallengeId)) {
        (backups ?? []).forEach((b) => b.challengeId && perChallengeBackupIdsOfUnselected.add(b.challengeId));
      }
    });

    const candidateIds = new Set<string>();
    const collect = (backups: ChallengeDescriptor[] | undefined, dropUnselectedPerChallenge = false) => {
      (backups ?? []).forEach((b) => {
        const id = b.challengeId;
        // A backup is only usable if it isn't the challenge itself and isn't already active.
        if (!id || id === challenge.challengeId || selectedChallengeIds.has(id)) {
          return;
        }
        if (dropUnselectedPerChallenge && perChallengeBackupIdsOfUnselected.has(id)) {
          return;
        }
        candidateIds.add(id);
      });
    };

    // This challenge's own per-challenge backups, plus the event's general backups.
    collect(backupConfig.perChallengeBackups?.[challenge.challengeId]);
    collect(backupConfig.generalBackups, true);

    return candidateIds.size;
  })();

  // Facilitators/owners/admins can replace a live challenge from the non-edit (read-only) view.
  const canReplaceWithBackup =
    config.ff.enableReplaceChallengeWithBackup &&
    isEvent &&
    !editMode &&
    !backupChallenges &&
    !!challenge.challengeId &&
    (target as Event).canHaveBackupChallenges &&
    !!user &&
    ((target as Event).isEventOwner(user) || (target as Event).isEventFacilitatorOrSupport(user));

  return (
    <Container
      data-testid={`challenge-card`}
      header={
        <Header
          variant="h2"
          actions={
            <div style={{ lineHeight: '30px' }}>
              <SpaceBetween direction="horizontal" size="s">
                <span>
                  {t(i18nKeys.challenges.browseChallenges.headers.rating)}:
                  <StarRating
                    value={challenge?.globalStatistics?.rating}
                    valueCount={challenge?.globalStatistics?.ratingCount}
                  />
                </span>
                {canReplaceWithBackup && (
                  <div className="left-border" style={{ paddingLeft: '8px' }}>
                    <Button
                      disabled={availableBackupCount === 0}
                      disabledReason={
                        availableBackupCount === 0
                          ? t(i18nKeys.challenges.backupChallenges.replaceWithBackup.noBackupsTooltip)
                          : undefined
                      }
                      onClick={() => setReplaceBackupModalVisible(true)}
                      data-testid={`challenge-card__replace-backup-btn-${index + 1}`}
                    >
                      {t(i18nKeys.challenges.backupChallenges.replaceWithBackup.button)}
                    </Button>
                  </div>
                )}
                {canRemoveChallenge && (
                  <div className="left-border">
                    <Button
                      iconName="status-negative"
                      variant="link"
                      onClick={() => handleRemoveChallengeAction(challenge.challengeId as string)}
                      data-testid={`challenge-card__remove-btn-${index + 1}`}
                    >
                      {t(i18nKeys.general.remove)}
                    </Button>
                  </div>
                )}
              </SpaceBetween>
            </div>
          }
        >
          {`${index + 1}. ${challenge.props.title}`}{' '}
          <Button
            variant="inline-link"
            data-testid={`challenge-card__toggle-challenge-info-btn-${index + 1}`}
            onClick={() => toggleChallengeInfo(challenge)}
          >
            {t(i18nKeys.general.info)}
          </Button>
        </Header>
      }
    >
      {challenge.isDefective && (
        <Box margin={{ bottom: 'l' }}>
          <SpaceBetween direction="vertical" size="xs">
            <span>
              <Icon name="status-warning" variant="error" />{' '}
              {t(i18nKeys.challenges.browseChallenges.messages.challengeIsDefective)}
            </span>
          </SpaceBetween>
        </Box>
      )}

      <ColumnLayout columns={4}>
        <KeyValue secondaryLabelColor label={t(i18nKeys.challenges.browseChallenges.headers.difficulty)}>
          <DifficultyIndicator challenge={challenge} challengeDescriptor={challengeDescriptor ?? undefined} />
        </KeyValue>
        <KeyValue secondaryLabelColor label={t(i18nKeys.challenges.browseChallenges.headers.lastUsed)}>
          {challenge.globalStatistics.lastUsed ? (
            <LocalizedTimeAgo datetime={challenge.globalStatistics?.lastUsed} />
          ) : (
            t(i18nKeys.general.nA)
          )}
        </KeyValue>
        <KeyValue secondaryLabelColor label={t(i18nKeys.challenges.browseChallenges.headers.type)}>
          <div>{challenge.props.jamType}</div>
        </KeyValue>
        <KeyValue secondaryLabelColor label={t(i18nKeys.challenges.browseChallenges.headers.poweredBy)}>
          <div>{formatLabProviderLabel(challenge.props.defaultLabProvider)}</div>
        </KeyValue>
        {!backupChallenges && getChallengePointsPossible && (
          <KeyValue secondaryLabelColor label={t(i18nKeys.challenges.browseChallenges.headers.points)}>
            <div>{getChallengePointsPossible(challenge)}</div>
          </KeyValue>
        )}
        <KeyValue secondaryLabelColor label={t(i18nKeys.challenges.browseChallenges.headers.solvedRecently)}>
          <div>
            {!challenge.globalStatistics.solvedRecently() ? (
              <span role="img" aria-label={t(i18nKeys.general.warning)}>
                <Icon name="status-warning" variant="warning" /> {t(i18nKeys.general.no)}
              </span>
            ) : (
              <span>{t(i18nKeys.general.yes)}</span>
            )}
          </div>
        </KeyValue>
        <KeyValue secondaryLabelColor label={t(i18nKeys.challenges.challengeDetails.titles.category)}>
          <div>{challenge.props.category}</div>
        </KeyValue>
        <SpaceBetween size="s" direction="vertical">
          {challenge.hasCfnTemplate && (
            <Link
              href={`/labs?${isEvent ? `event-name=${event?.name}` : `campaign-id=${campaign?.id}`}challenge-id=${
                challenge?.id
              }}`}
            >
              {t(i18nKeys.challenges.browseChallenges.headers.viewLabs)}
            </Link>
          )}
          {advancedMode && (
            <Button
              variant="inline-link"
              onClick={handleDownloadImportedCSV}
              iconName="download"
              iconAlign="right"
              data-testid="challenge-card__download-csv-btn"
            >
              {t(i18nKeys.challenges.challengeSelection.labels.downloadImportedCSV)}
            </Button>
          )}
        </SpaceBetween>
        {advancedMode && (
          <KeyValue secondaryLabelColor label={t(i18nKeys.challenges.challengeSelection.labels.avgSolveTime)}>
            {challenge?.globalStatistics.solveTimes.trimmedAvgSeconds
              ? getDuration(challenge?.globalStatistics.solveTimes.trimmedAvgSeconds)
              : t(i18nKeys.general.nA)}
          </KeyValue>
        )}
        {advancedMode && (
          <KeyValue secondaryLabelColor label={t(i18nKeys.challenges.challengeSelection.labels.lastSolved)}>
            {challenge?.globalStatistics.lastSolved ? (
              <LocalizedTimeAgo datetime={challenge?.globalStatistics.lastSolved} />
            ) : (
              t(i18nKeys.general.nA)
            )}
          </KeyValue>
        )}
        {advancedMode && (
          <KeyValue secondaryLabelColor label={t(i18nKeys.challenges.challengeSelection.labels.awsServices)}>
            {challenge?.props.awsServices.map((awsService, i) => {
              if (i + 1 !== challenge?.props.awsServices.length) {
                return `${awsService}, `;
              } else {
                return `${awsService}`;
              }
            })}
          </KeyValue>
        )}
        {advancedMode && (
          <KeyValue secondaryLabelColor label={t(i18nKeys.challenges.challengeSelection.labels.tags)}>
            <SpaceBetween direction="horizontal" size="xs">
              {challenge.props.tags.map((tag, i) => {
                return <Badge key={`tag-${i}`}>{tag}</Badge>;
              })}
            </SpaceBetween>
          </KeyValue>
        )}
        {advancedMode && (
          <KeyValue secondaryLabelColor label={t(i18nKeys.challenges.challengeSelection.labels.avgDeployTime)}>
            {challenge?.hasStack
              ? getDuration(challenge?.avgStackDeployTime)
              : t(i18nKeys.challenges.challengeSelection.labels.hasNoStack)}
          </KeyValue>
        )}
        {advancedMode && (
          <KeyValue secondaryLabelColor label={t(i18nKeys.challenges.challengeDetails.titles.warmup)}>
            {warmupChallenge ? t(i18nKeys.general.yes) : t(i18nKeys.general.no)}
          </KeyValue>
        )}
        {target instanceof Event && (
          <Toggle
            disabled={!canEditWithAttribute('codeWhispererDisabled')}
            checked={challengeDescriptor?.codeWhispererDisabled ?? false}
            data-testid={`challenge-card__codewhisperer-toggle-${index}`}
            onChange={() => toggleCodeWhispererDisabled()}
          >
            {t(i18nKeys.challenges.challengeSelection.advancedSettings.labels.disableCodeWhisperer)}
          </Toggle>
        )}
      </ColumnLayout>
      {advancedMode && !backupChallenges && getDefaultChallengePointsPossible && (
        <ChallengeCardAdvanced
          challenge={challenge}
          getDefaultChallengePointsPossible={getDefaultChallengePointsPossible}
          target={target}
          canEditWithAttribute={canEditWithAttribute}
          challengeDescriptor={challengeDescriptor}
          handleEditEvent={handleEditEvent}
        />
      )}
      {target instanceof Event && backupChallenges && challenge.challengeId && (
        <>
          <HorizontalRule />
          <PerChallengeBackupChallenges target={target} challengeId={challenge.challengeId} />
        </>
      )}
      {canReplaceWithBackup && challenge.challengeId && (
        <ReplaceChallengeWithBackupModal
          visible={replaceBackupModalVisible}
          onDismiss={() => setReplaceBackupModalVisible(false)}
          target={target as Event}
          challengeIdToReplace={challenge.challengeId}
          challengeTitle={challenge.props.title ?? ''}
        />
      )}
    </Container>
  );
};
export default ChallengeCard;
